import React, { useEffect, useState } from "react";
import StatusDropdown from "../Shared/StatusDropdown";
import { useNavigate } from "react-router-dom";
import { dropdownCategories, dropdownProducts } from "../../api/dropdownService";
import CommonDatePicker from "../../common/CommonDatePicker";
import FullPageLoader from "../../common/FullPageLoader";

const formatDateForApi = (date) => {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};

export default function CouponsForm({
  onSubmit,
  loading = false,
  isEdit = false,
  initialData = {},
}) {
  const navigate = useNavigate();
  const initialState = {
    title: "",
    code: "",
    description: "",
    discountType: "FLAT",
    applicableCategory: "",
    applicableProduct: [],
    discountValue: "",
    minOrderAmount: "",
    maxOrderAmount: "",
    maxDiscountAmount: "",
    startDate: "",
    expiryDate: "",
    totalUsageLimit: "",
    status: "ACTIVE",
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      const response = await dropdownCategories();
      const categoryList =
        response?.data?.categories ||
        response?.data?.data ||
        response?.categories ||
        response?.data ||
        response ||
        [];
      setCategories(Array.isArray(categoryList) ? categoryList : []);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!formData.applicableCategory) {
      setProducts([]);
      setProductsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchProducts = async () => {
      setProductsLoading(true);
      const response = await dropdownProducts("", formData.applicableCategory);
      const productList =
        response?.data?.products ||
        response?.data?.data ||
        response?.products ||
        response?.data ||
        response ||
        [];
      if (!cancelled) {
        setProducts(Array.isArray(productList) ? productList : []);
        setProductsLoading(false);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [formData.applicableCategory]);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        code: initialData.code || "",
        title: initialData.title || "",
        description: initialData.description || "",
        discountType: initialData.discountType || "FLAT",
        applicableCategory: initialData.applicableCategory?._id || initialData.applicableCategory || "",
        applicableProduct: Array.isArray(initialData.applicableProducts || initialData.applicableProduct)
          ? (initialData.applicableProducts || initialData.applicableProduct).map((product) => String(product?._id || product))
          : (initialData.applicableProducts || initialData.applicableProduct)
            ? [String((initialData.applicableProducts || initialData.applicableProduct)?._id || initialData.applicableProducts || initialData.applicableProduct)]
            : [],
        discountValue: initialData.discountValue || "",
        minOrderAmount: initialData.minOrderAmount || "",
        maxOrderAmount: initialData.maxOrderAmount || "",
        maxDiscountAmount: initialData.maxDiscountAmount || "",
        startDate: initialData.startDate
          ? initialData.startDate.split("T")[0]
          : "",
        expiryDate: initialData.expiryDate
          ? initialData.expiryDate.split("T")[0]
          : "",
        totalUsageLimit: initialData.totalUsageLimit || "",
        status: initialData.status || "ACTIVE",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "applicableCategory") setProductDropdownOpen(false);

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "applicableCategory" && { applicableProduct: [] }),
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleProductToggle = (productId, checked) => {
    const selectedProductId = String(productId);
    setFormData((prev) => ({
      ...prev,
      applicableProduct: checked
        ? prev.applicableProduct.includes(selectedProductId)
          ? prev.applicableProduct
          : [...prev.applicableProduct, selectedProductId]
        : prev.applicableProduct.filter((id) => id !== selectedProductId),
    }));
    setErrors((prev) => ({ ...prev, applicableProducts: "", applicableProduct: "" }));
  };

  //  SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    const dateErrors = {};
    if (!formData.startDate) dateErrors.startDate = "Start date is required";
    if (!formData.expiryDate) dateErrors.expiryDate = "Expiry date is required";
    if (Object.keys(dateErrors).length) {
      setErrors(dateErrors);
      return;
    }

    if (formData.totalUsageLimit !== "" && Number(formData.totalUsageLimit) < 0) {
      setErrors((prev) => ({
        ...prev,
        totalUsageLimit: "Total usage limit cannot be negative",
      }));
      return;
    }

    const payload = {
      code: formData.code,
      title: formData.title,
      description: formData.description,
      discountType: formData.discountType.toUpperCase(),
      applicableCategory: formData.applicableCategory,
      applicableProducts: formData.applicableProduct,
      discountValue: Number(formData.discountValue),
      minOrderAmount: Number(formData.minOrderAmount),
      ...(formData.maxOrderAmount !== "" && {
        maxOrderAmount: Number(formData.maxOrderAmount),
      }),
      ...(formData.discountType === "FLAT" && {
        maxDiscountAmount: Number(formData.maxDiscountAmount),
      }),
      startDate: formatDateForApi(formData.startDate),
      expiryDate: formatDateForApi(formData.expiryDate),
      ...(formData.totalUsageLimit !== "" && {
        totalUsageLimit: Number(formData.totalUsageLimit),
      }),
      status: formData.status,
    };


    try {
      setErrors({});

      const response = await onSubmit(payload);

    

      if (response?.status === 422 || response?.success === false) {
        setErrors(response.error || {});
        return;
      }

      setSuccessMessage(
        isEdit ? "Coupon Updated Successfully" : "Coupon Added Successfully",
      );

      if (!isEdit) {
        setFormData(initialState);
      }

      setTimeout(() => {
        setSuccessMessage("");
        navigate("/coupon");
      }, 2000);
    } catch (error) {
      
    }
  };

  return (
    <div className="container-fluid p-4">
      {loading && <FullPageLoader text="Saving..." />}
      <div className="card border-2"
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "20px",
        }}
      >
        

        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Coupon title <span className="error">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="form-control"
                placeholder="Enter coupon title"
                value={formData.title}
                onChange={handleChange}
              />
              {errors.title && <small className="error">{errors.title}</small>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Coupon code <span className="error">*</span>
              </label>
              <input
                type="text"
                name="code"
                className="form-control"
                placeholder="Enter coupon code"
                value={formData.code}
                onChange={handleChange}
              />
              {errors.code && <small className="error">{errors.code}</small>}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Discount type <span className="error">*</span>
              </label>
              <select
                name="discountType"
                className="form-select np-status-dropdown"
                value={formData.discountType}
                onChange={handleChange}
              >
                <option value="FLAT">Flat</option>
                <option value="PERCENTAGE">Percentage</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Discount value
              </label>
              <input
                type="number"
                name="discountValue"
                className="form-control"
                placeholder="Enter discount value"
                value={formData.discountValue}
                onChange={handleChange}
              />
              {errors.discountValue && (
                <small className="error">{errors.discountValue}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Min order amount
              </label>
              <input
                type="number"
                name="minOrderAmount"
                className="form-control"
                placeholder="Enter minimum order amount"
                value={formData.minOrderAmount}
                onChange={handleChange}
              />
              {errors.minOrderAmount && (
                <small className="error">{errors.minOrderAmount}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Max order amount <span className="error">*</span>
              </label>
              <input
                type="number"
                name="maxOrderAmount"
                className="form-control"
                placeholder="Enter maximum order amount"
                value={formData.maxOrderAmount}
                onChange={handleChange}
              />
              {errors.maxOrderAmount && (
                <small className="error">{errors.maxOrderAmount}</small>
              )}
            </div>

            {formData.discountType === "PERCENTAGE" && <div className="col-md-4">
              <label className="form-label fw-semibold">
                Max discount <span className="error">*</span>
              </label>

              <input
                type="number"
                name="maxDiscountAmount"
                className="form-control"
                placeholder="Enter maximum discount"
                value={formData.maxDiscountAmount}
                onChange={handleChange}
              />

              {errors.maxDiscountAmount && (
                <small className="error">{errors.maxDiscountAmount}</small>
              )}
            </div>}


            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Applicable category <span className="error">*</span>
              </label>

              <select
                name="applicableCategory"
                className="form-select"
                value={formData.applicableCategory}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name || category.title || category._id}
                  </option>
                ))}
              </select>

              {errors.applicableCategory && (
                <small className="error">{errors.applicableCategory}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Applicable product
              </label>
              <div className="dropdown w-100">
                <button
                  type="button"
                  className="form-select text-start"
                  disabled={!formData.applicableCategory || productsLoading}
                  aria-expanded={productDropdownOpen}
                  onClick={() => setProductDropdownOpen((open) => !open)}
                >
                  {!formData.applicableCategory
                    ? "Select a category first"
                    : productsLoading
                      ? "Loading products..."
                      : formData.applicableProduct.length
                        ? `${formData.applicableProduct.length} product(s) selected`
                        : "Select products"}
                </button>
                {productDropdownOpen && formData.applicableCategory && !productsLoading && (
                  <div className="dropdown-menu show p-2" style={{ width: "100%", maxHeight: "240px", overflowY: "auto" }}>
                    {products.length ? products.map((product) => {
                      const productId = product._id || product.id || product.productId;
                      const checkboxId = `coupon-product-${productId}`;

                      return (
                      <div key={productId} className=" d-flex align-items-center gap-2">
                        <input
                          id={checkboxId}
                          type="checkbox"
                          className="m-0 flex-shrink-0"
                          style={{
                            appearance: "auto",
                            backgroundColor: "initial",
                            accentColor: "#0d6efd",
                            width: "16px",
                            height: "16px",
                          }}
                          checked={formData.applicableProduct.includes(String(productId))}
                          onChange={(event) => handleProductToggle(productId, event.target.checked)}
                        />
                        <label htmlFor={checkboxId} className="mb-0 flex-grow-1">
                          {product.productName || product.name || product.title || productId}
                        </label>
                      </div>
                    )}) : (
                      <span className="dropdown-item-text">No products available</span>
                    )}
                  </div>
                )}
              </div>
              {(errors.applicableProducts || errors.applicableProduct) && (
                <small className="error">
                  {errors.applicableProducts || errors.applicableProduct}
                </small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Start date <span className="error">*</span>
              </label>

              <CommonDatePicker
                name="startDate"
                className="form-control"
                value={formData.startDate}
                onChange={handleChange}
                dateFormat="dd-MM-yyyy"
                placeholder="DD-MM-YYYY"
              />

              {errors.startDate && (
                <small className="error">{errors.startDate}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Expiry date<span className="error">*</span>
              </label>
              <CommonDatePicker
                name="expiryDate"
                className="form-control"
                value={formData.expiryDate}
                onChange={handleChange}
                dateFormat="dd-MM-yyyy"
                placeholder="DD-MM-YYYY"
              />
              {errors.expiryDate && (
                <small className="error">{errors.expiryDate}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                 Total usage limit <span className="error">*</span>
              </label>
              <input
                type="number"
                name="totalUsageLimit"
                className="form-control"
                min="0"
                placeholder="Enter total usage limit"
                value={formData.totalUsageLimit}
                onChange={handleChange}
              />
              {errors.totalUsageLimit && (
                <small className="error">{errors.totalUsageLimit}</small>
              )}
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">
                Status
              </label>
              <StatusDropdown
                name="status"
                className="form-control"
                value={formData.status}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label fw-semibold">
                Coupon description
              </label>
              <textarea
                rows="4"
                name="description"
                className="form-control"
                placeholder="Enter coupon description"
                value={formData.description}
                onChange={handleChange}
              />
              {errors.description && (
                <small className="error">{errors.description}</small>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Submitting..."
                : isEdit
                  ? "Update Coupon"
                  : "Add Coupon"}
            </button>

            <button
              type="button"
              className="btn"
              style={{ backgroundColor: "#032B4D", color: "#fff" }}
              onClick={() => navigate("/coupon")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
